import { Heading, VStack, chakra, useToast } from "@chakra-ui/react";
import {
  DeleteAction,
  FeatureAction,
  PerformerDraggable,
} from "./PerformerRow";
import { Reorder } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import AddArtistInput from "./AddArtistInput";
import actions from "../../../actions";
import {
  KYDPerformerDisplay,
  KYDPerformer,
  EventDetailLineupSection,
} from "@common/interfaces/KYDEvent";
import { useLoading } from "../../hooks/form";
import { EditModalType } from "../../../utils";
import { useDialog } from "../../../Common/Dialog";
import { SectionProps } from "../sections/sectionsComponents";
import GenresSection from "../Genres/GenresSection";

export type Performer = Partial<KYDPerformer>;

const List = chakra(Reorder.Group);

const usePerformersSearch = (eventId?: string) => {
  const [searchResults, setSearchResults] = useState<Performer[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const search = useCallback(
    async (query: string) => {
      if (!eventId || !query) return;

      try {
        setIsLoading(true);
        const results = await actions.ADMIN.searchPerformers(eventId, query);
        setSearchResults(results.items);
      } catch (e) {
        console.log("Could not fetch search results", e);
      } finally {
        setIsLoading(false);
      }
    },
    [eventId]
  );

  return {
    search,
    reset: () => setSearchResults([]),
    results: searchResults,
    loading: isLoading,
  };
};

const LineupSection = ({
  data,
  setModalType,
  permissions,
  isLoading,
  refetchEvent,
}: SectionProps<EventDetailLineupSection["data"]>) => {
  const [newsOrder, setNewsOrder] = useState<string[]>([]);
  const newsOrderRef = useRef(null);
  const [lineup, setLineup] = useState<KYDPerformerDisplay[]>(
    data.performers || []
  );
  const dialog = useDialog();
  const toast = useToast();

  const {
    results: searchResults,
    search,
    loading: searchLoading,
    reset,
  } = usePerformersSearch(data.id);
  const [loading, updateLoading] = useLoading({ performers: false, genres: false });

  const onUpdatePerformers = useCallback(
    async (performers: KYDPerformerDisplay[]) => {
      try {
        await actions.ADMIN.updatePerformers(data.id, performers);
        await refetchEvent({ silent: true });
        setModalType(EditModalType.INACTIVE);

        toast({
          title: `Lineup updated`,
          description: `Lineup order has been updated`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        await refetchEvent({ silent: true });
        dialog({ text: err.message });
      }
    },
    [data, refetchEvent, setModalType]
  );

  const onAddPerformer = useCallback(
    async ({
      name,
      soundcharts_id,
    }: {
      name: string;
      soundcharts_id: string;
    }) => {
      try {
        await actions.ADMIN.createPerformer(data.id, soundcharts_id);
        await refetchEvent({ silent: true });
        setModalType(EditModalType.INACTIVE);

        toast({
          title: `${name} added`,
          description: `${name} has been added to the lineup`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        await refetchEvent({ silent: true });
        dialog({ text: err.message });
      }
    },
    [data, refetchEvent, setModalType]
  );

  const onDeletePerformer = useCallback(
    async (performer: KYDPerformerDisplay) => {
      try {
        await actions.ADMIN.deletePerformer(data.id, performer.id);
        await refetchEvent({ silent: true });
        setModalType(EditModalType.INACTIVE);

        toast({
          title: `${performer.name} removed`,
          description: `${performer.name} has been removed from the lineup`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
    },
    [data, setModalType, refetchEvent]
  );

  useEffect(() => {
    setNewsOrder(lineup.map((p) => p.id));
  }, [lineup]);

  useEffect(() => {
    if (data?.performers) {
      setLineup(data.performers);
    }
  }, [data]);

  useEffect(() => {
    //@ts-ignore
    newsOrderRef.current = newsOrder;
  }, [newsOrder]);

  const onUpdateOrder = async () => {
    const newOrder = newsOrderRef.current;
    const performersInNewOrder = newOrder.map((no) =>
      lineup.find((p) => p.id === no)
    );

    setLineup(performersInNewOrder);
    updateLoading("performers", true);

    await onUpdatePerformers(performersInNewOrder);

    updateLoading("performers", false);
  };

  const onSearch = async (query: string) => {
    search(query);
  };

  const onUpdateGenres = useCallback(
    async (genres: { name: string; id?: string }[]) => {
      try {
        updateLoading("genres", true);
        await actions.ADMIN.updateGenres(data.id, genres);
        await refetchEvent({ silent: true });

        toast({
          title: `Genres updated!`,
          description: `The genres of your event has been updated.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("genres", false);
    },
    [data.id, updateLoading]
  );

  const onAddToLineup = async ({
    name,
    image_url,
    soundcharts_id,
  }: {
    name: string;
    image_url: string;
    soundcharts_id: string;
  }) => {
    const softPerformer: KYDPerformerDisplay = {
      name,
      id: soundcharts_id,
      image_url,
      headliner: false,
    };

    setLineup((oldLineup) => oldLineup.concat(softPerformer));
    reset();

    updateLoading("performers", true);

    await onAddPerformer({ name, soundcharts_id });

    updateLoading("performers", false);
  };

  const onClickFeature = async (
    performer: KYDPerformerDisplay,
    isHeadliner: boolean
  ) => {
    const newPerformers = lineup.map((oldP) => {
      if (oldP.id === performer.id) {
        return {
          ...performer,
          headliner: isHeadliner,
        };
      }

      return oldP;
    });

    setLineup(newPerformers);

    updateLoading("performers", true);

    await onUpdatePerformers(newPerformers);

    updateLoading("performers", false);
  };

  const hasPermission = (permissionToCheck) =>
    permissions.includes(permissionToCheck);

  const onClickDelete = async (performer: KYDPerformerDisplay) => {
    const newPerformers = lineup.filter((oldP) => oldP.id !== performer.id);
    setLineup(newPerformers);
    updateLoading("performers", true);

    await onDeletePerformer(performer);

    updateLoading("performers", false);
  };

  return (
    <VStack w="full">
      <Heading size="sm" w="full">
        Line-up
      </Heading>
      {newsOrder.length ? (
        <List
          draggable={false}
          axis="y"
          onReorder={setNewsOrder}
          values={newsOrder}
          listStyleType={"none"}
          w="full"
        >
          <VStack
            my={4}
            sx={{
              opacity: loading.performers ? 0.5 : 1,
              pointerEvents: loading.performers ? "none" : "auto",
            }}
          >
            {newsOrder
              .map((no) => lineup.find((p) => p.id === no))
              .map((p) =>
                p ? (
                  <PerformerDraggable
                    readOnly={!hasPermission("write")}
                    onUpdate={hasPermission("write") ? onUpdateOrder : () => {}}
                    key={`${p.id}-${p.name}`}
                    name={p.name}
                    platforms={[p.instagram, p.spotify, p.tiktok]}
                    image={p.image_url}
                    id={p.id}
                    actions={
                      hasPermission("write")
                        ? [
                            <FeatureAction
                              key={`feature-${p.id}`}
                              isActive={p.headliner}
                              isDisabled={isLoading || loading.performers}
                              onClick={() => onClickFeature(p, !p.headliner)}
                              name={p.name}
                            />,
                            <DeleteAction
                              key={`delete-${p.id}`}
                              isDisabled={isLoading || loading.performers}
                              onClick={() => onClickDelete(p)}
                              name={p.name}
                            />,
                          ]
                        : []
                    }
                  />
                ) : null
              )}
          </VStack>
        </List>
      ) : null}
      {hasPermission("write") ? (
        <AddArtistInput
          isDisabled={isLoading}
          isLoading={searchLoading}
          onSearch={onSearch}
          searchResults={searchResults}
          onConfirm={onAddToLineup}
        />
      ) : null}
      <GenresSection
        readOnly={!hasPermission('write')}
        data={{
          genres: data.genres,
        }}
        isLoading={isLoading || loading.genres}
        onSave={onUpdateGenres}
      />
    </VStack>
  );
};

export default LineupSection;
